import React, { useState, useRef, useEffect, useMemo } from 'react';
import './FilterableSelect.scss';

export const FilterableSelect = ({
    options = [],
    value = '',
    onChange,
    placeholder = 'Select an option...',
    searchPlaceholder = 'Search...',
    className = '',
    disabled = false,
    clearable = true,
    maxHeight = '80vh',
    renderOption,
    getOptionLabel = (option) => option.label || option.name || option,
    getOptionValue = (option) => option.value || option.id || option,
    noOptionsText = 'No options found',
    allowCustomValues = false,
    ...props
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const containerRef = useRef(null);
    const searchInputRef = useRef(null);

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isOpen]);

    // Focus search input when dropdown opens
    useEffect(() => {
        if (isOpen && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [isOpen]);

    // Filter options based on search term
    const filteredOptions = useMemo(() => {
        if (!searchTerm) return options;

        return options.filter(option => {
            const label = getOptionLabel(option).toLowerCase();
            return label.includes(searchTerm.toLowerCase());
        });
    }, [options, searchTerm, getOptionLabel]);

    // Get selected option with full data
    const selectedOption = useMemo(() => {
        return options.find(option => getOptionValue(option) === value) || null;
    }, [options, value, getOptionValue]);

    const handleSelectOption = (option) => {
        const optionValue = getOptionValue(option);
        onChange(optionValue);
        setIsOpen(false);
        setSearchTerm('');
    };

    const handleClear = (event) => {
        event.stopPropagation();
        onChange('');
        setSearchTerm('');
    };

    const toggleDropdown = () => {
        if (!disabled) {
            setIsOpen(!isOpen);
            if (!isOpen) {
                setSearchTerm('');
            }
        }
    };

    const handleKeyDown = (e, option = null) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (option) {
                handleSelectOption(option);
            } else {
                toggleDropdown();
            }
        }
        if (e.key === 'Escape') {
            setIsOpen(false);
            setSearchTerm('');
        }
    };

    const defaultRenderOption = (option, isSelected) => (
        <div className={`filterable-select__option ${isSelected ? 'filterable-select__option--selected' : ''}`}>
            <span className="filterable-select__option-label">
                {getOptionLabel(option)}
            </span>
            {isSelected && (
                <span className="filterable-select__option-check">✓</span>
            )}
        </div>
    );

    return (
        <div
            ref={containerRef}
            className={`filterable-select ${className} ${disabled ? 'filterable-select--disabled' : ''} ${isOpen ? 'filterable-select--open' : ''}`}
            {...props}
        >
            <div
                className="filterable-select__control"
                onClick={toggleDropdown}
                role="button"
                tabIndex={disabled ? -1 : 0}
                onKeyDown={handleKeyDown}
                aria-expanded={isOpen}
                aria-haspopup="listbox"
            >
                <div className="filterable-select__value-container">
                    {selectedOption ? (
                        <span className="filterable-select__single-value">
                            {getOptionLabel(selectedOption)}
                        </span>
                    ) : (
                        <span className="filterable-select__placeholder">{placeholder}</span>
                    )}
                </div>

                <div className="filterable-select__indicators">
                    {clearable && selectedOption && (
                        <button
                            type="button"
                            className="filterable-select__clear-indicator"
                            onClick={handleClear}
                            aria-label="Clear selection"
                        >
                            ×
                        </button>
                    )}
                    <div className="filterable-select__dropdown-indicator">
                        <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M5 7.5L10 12.5L15 7.5"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </div>
                </div>
            </div>

            {isOpen && (
                <div className="filterable-select__menu" style={{ maxHeight }}>
                    <div className="filterable-select__search">
                        <input
                            ref={searchInputRef}
                            type="text"
                            className="filterable-select__search-input"
                            placeholder={searchPlaceholder}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && allowCustomValues && searchTerm) {
                                    e.preventDefault();
                                    onChange(searchTerm);
                                    setIsOpen(false);
                                    setSearchTerm('');
                                }
                            }}
                        />
                    </div>

                    <div className="filterable-select__options">
                        {filteredOptions.length === 0 ? (
                            <div className="filterable-select__no-options">
                                {allowCustomValues && searchTerm ? (
                                    <div
                                        className="filterable-select__create-option"
                                        onClick={() => {
                                            onChange(searchTerm);
                                            setIsOpen(false);
                                            setSearchTerm('');
                                        }}
                                        role="option"
                                    >
                                        Create "{searchTerm}"
                                    </div>
                                ) : (
                                    noOptionsText
                                )}
                            </div>
                        ) : (
                            filteredOptions.map((option) => {
                                const optionValue = getOptionValue(option);
                                const isSelected = value === optionValue;

                                return (
                                    <div
                                        key={optionValue}
                                        className="filterable-select__option-wrapper"
                                        onClick={() => handleSelectOption(option)}
                                        onKeyDown={(e) => handleKeyDown(e, option)}
                                        role="option"
                                        tabIndex={0}
                                        aria-selected={isSelected}
                                    >
                                        {renderOption
                                            ? renderOption(option, isSelected)
                                            : defaultRenderOption(option, isSelected)
                                        }
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default FilterableSelect;
